<script setup lang="ts">
/**
 * Singbac — Incorporation Pre Studio panel.
 * Live document package (3종: Draft Constitution · Form 45B Consent · Notice to Controller)
 * + 엔티티 자동바인딩(회사·비서·주주 필드). Incorporation Post 패널을 복제하되 PRE 3종 문서에 맞춰 축약
 * (노미니/서명(Signers) 로직 없음, controller 는 registrable-controller 부수 ctrl_{id}/).
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideCddFill, bareKey, typeOf } from '../incorporation-pre/cdd/useCddFill';
import {
  provideSgIncPreSigners,
  buildOneSigner,
} from '../incorporation-pre/cdd/sgIncPreSignersContext';
import SingbacIncorporationPrePackageDoc from '../incorporation-pre/SingbacIncorporationPrePackageDoc.vue';
import { normalizeSingbacIncPreFormDataForWord } from '../utils/normalizeSingbacIncPreFormDataForWord';
import { mapCustomFormAnswerToSingbacPre } from '../mappers/mapCustomFormAnswerToSingbacPre';
import { customFormApi, type CustomFormQuestion } from '@/features/setupEntity/api/customFormApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import {
  loadCmBaseCodeOptions,
  nationalityLabelForOutput,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import '../incorporation-pre/allotDoc.css';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideCddFill();
// 문서별 [[Signers]] — 부수 스코프(dir_/sec_/ctrl_)마다 독립 서명자.
const signersCtx = provideSgIncPreSigners();
const docRef = ref<InstanceType<typeof SingbacIncorporationPrePackageDoc> | null>(null);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const autofilledKeys = ref<Set<string>>(new Set());

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

/**
 * 필드 키 → officer 조회 role.
 *  · 회사명            → 엔티티 재선택 (incorporation)
 *  · 비서(Form45B)     → SECRETARY (엔티티 비서)
 *  · subscriber        → SHAREHOLDER (엔티티 멤버)
 *  · 서명 이사(Notice)  → DIRECTOR (엔티티 이사)
 *  · controller / 그 외 → CONTACT (client-wide contacts)
 */
function lookupForKey(bare: string): {
  defaultRole: string;
  lookupType: string;
  memberEntityKind?: 'individual' | 'corporate';
  entityCd: string;
} {
  if (bare === 'company_name' || bare === 'entity_name') {
    return { defaultRole: '', lookupType: 'incorporation', entityCd: '' };
  }
  if (/secretary/.test(bare)) {
    return { defaultRole: 'SECRETARY', lookupType: 'officer', entityCd: entityCd.value || '' };
  }
  if (/subscriber_name$/.test(bare)) {
    return {
      defaultRole: 'SHAREHOLDER',
      lookupType: 'officer',
      memberEntityKind: 'individual',
      entityCd: entityCd.value || '',
    };
  }
  if (bare === 'director_name') {
    return { defaultRole: 'DIRECTOR', lookupType: 'officer', entityCd: entityCd.value || '' };
  }
  // controller_* 및 그 외 인물 필드 — client-wide contacts
  return { defaultRole: 'CONTACT', lookupType: 'officer', entityCd: '' };
}

fill.setActivateHandler((key: string) => {
  const bare = bareKey(key);
  const t = typeOf(key);
  if (t === 'address') {
    emit('request-address-input', key);
    return;
  }
  if (t !== 'entity' && t !== 'person') return;

  const spec = lookupForKey(bare);
  emit('request-person-lookup', {
    fieldGroup: 'singbac-incorporation-pre',
    key: bare,
    scopedKey: key,
    defaultRole: spec.defaultRole || undefined,
    lookupType: spec.lookupType,
    entityCd: spec.entityCd || '',
    ...(spec.memberEntityKind ? { memberEntityKind: spec.memberEntityKind } : {}),
  });
});

fill.setAddressHandler((key: string) => {
  emit('request-address-input', key);
});

watch(
  () => props.appliedAddress,
  (a) => {
    if (!a?.key) return;
    const line = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join(', ');
    if (line) fill.setValue(a.key, line);
  },
);

/** contactCd → applyPersonToKey 가 기대하는 정규화 person 객체. */
async function enrichContact(contactCd: string): Promise<Record<string, any> | null> {
  const cd = String(contactCd || '').trim();
  if (!cd) return null;
  try {
    const raw: any = await contactsApi.getContactById(cd);
    const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
    return {
      ...c,
      name:
        c.name || c.contactName || c.CONTACT_NAME ||
        [c.FIRST_NAME, c.MIDDLE_NAME, c.SURNAME].filter(Boolean).join(' ') ||
        [c.firstName, c.middleName, c.surname].filter(Boolean).join(' '),
      contactRegNo: c.contactRegNo ?? c.CONTACT_REG_NO ?? c.nricNo ?? c.passportNo,
      dateOfBirth: c.dateOfBirth ?? c.DATE_OF_BIRTH,
      BLOCK_NO: c.BLOCK_NO ?? c.blockNo,
      STREET_NAME: c.STREET_NAME ?? c.streetName,
      BUILDING_NAME: c.BUILDING_NAME ?? c.buildingName,
      ADDR_LEVEL: c.ADDR_LEVEL ?? c.addrLevel,
      ADDR_UNIT: c.ADDR_UNIT ?? c.addrUnit,
      POSTAL_CODE: c.POSTAL_CODE ?? c.postalCode,
      FOREIGN_ADDRESS_LINE_1: c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1,
      FOREIGN_ADDRESS_LINE_2: c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2,
      primaryAddressParts: c.primaryAddressParts,
      NATIONALITY_CD: c.NATIONALITY_CD ?? c.nationalityCd,
      nationality: c.NATIONALITY_CD ?? c.nationalityCd ?? c.nationality,
      nationalityName: c.nationalityName ?? c.nationalityText ?? c.NATIONALITY_NAME,
      aliases: c.aliases ?? c.alias ?? c.otherName ?? c.OTHER_NAME ?? c.aliasName,
    };
  } catch (err) {
    console.warn('[SingbacPre] contact enrich failed:', err);
    return null;
  }
}

watch(
  () => props.selectedPerson,
  async (p) => {
    if (!p) return;
    let person: Record<string, any> = { ...(p as any) };
    const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();
    if (contactCd) {
      const enriched = await enrichContact(contactCd);
      if (enriched) person = { ...enriched, ...person, ...enriched };
    }
    await fill.applyPersonToActive(person);
    emitProgress();
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Custom Form 연동 — pre-incorporation 은 등록 엔티티가 없다. 대신 클라이언트가 만든
//   **커스텀 폼 → 작성(제출) 항목** 을 **챗봇 위젯(custom-form-picker)** 에서 고르면,
//   챗 브리지가 formUpdates 로 넘긴 customFormCd/customFormAnswerCd 를 여기(chatFormData)에서 받아
//   작성 답변을 라벨 유사매칭으로 3종 문서에 자동 채운다. (setupEntity/customFormApi 사용)
// ─────────────────────────────────────────────────────────────────────────────
const lastAnswerCd = ref('');
/** 선택한 커스텀폼 응답(readable, 라벨:값) — 드래그 팔레트 소스. */
const answerReadable = ref<Record<string, any>>({});
/** 최신 매핑 부수(개수) — 문서 재마운트 시에도 반영되도록 보관 후 재적용. */
const pendingDocLocal = ref<{ controllerIds: number[]; secretaryIds: number[]; directorIds: number[]; subscriberIds?: number[] } | null>(null);
// 패키지 문서가 (재)마운트되면 최신 개수를 다시 적용.
watch(
  () => docRef.value,
  (d) => {
    if (d && pendingDocLocal.value) d.restoreLocalState?.(pendingDocLocal.value);
  },
);

async function autofillFromCustomFormAnswer(formCd: string, answerCd: string) {
  const fCd = String(formCd || '').trim();
  const aCd = String(answerCd || '').trim();
  if (!aCd || aCd === lastAnswerCd.value) return;
  lastAnswerCd.value = aCd;
  // 회사(응답) 재선택 → 이전 인물 데이터 완전 초기화(잔존/개수 불일치 방지).
  //   autofill 로 쓴 키뿐 아니라 person 스코프 키(dir_/sec_/ctrl_/sub{n}_)를 모두 비운다.
  const PERSON_SCOPE = /^(dir|sec|ctrl)_\d+\/|^sub\d+_/;
  for (const k of Object.keys(fill.values)) {
    if (PERSON_SCOPE.test(k) && String(fill.values[k] || '').trim()) fill.setValue(k, '');
  }
  if (autofilledKeys.value.size) {
    for (const k of autofilledKeys.value) fill.setValue(k, '');
    autofilledKeys.value = new Set();
  }
  try {
    const [detail, ans] = await Promise.all([
      fCd ? customFormApi.detail(fCd).catch(() => null) : Promise.resolve(null),
      customFormApi.answerDetail(aCd).catch(() => null),
    ]);
    // 응답은 승인본(운영 중) 폼 구조로 작성된다 — 승인 대기 수정본(formSchema)이 아니라 liveFormSchema 기준으로 매핑.
    const item: any = (detail as any)?.item || {};
    const liveSchema = item.liveFormSchema !== undefined ? item.liveFormSchema : item.formSchema;
    const questions: CustomFormQuestion[] = liveSchema?.questions || [];
    const raw = (ans as any)?.item?.answerJson?.raw || {};
    // 드래그 팔레트용 readable — 없으면 raw 라벨화 폴백(라벨=질문라벨).
    answerReadable.value = (ans as any)?.item?.answerJson?.readable || raw || {};
    const mapped = mapCustomFormAnswerToSingbacPre(questions, raw);

    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      const s = String(v ?? '').trim();
      if (s) {
        fill.setValue(k, s);
        written.add(k);
      }
    }
    autofilledKeys.value = written;

    await nextTick();
    // 새 인물 개수(부수) 적용. 문서가 아직 마운트 전이면 pendingDocLocal 에 저장 후 watch 로 재적용.
    pendingDocLocal.value = {
      controllerIds: mapped.local.controllerIds,
      secretaryIds: mapped.local.secretaryIds,
      directorIds: mapped.local.directorIds,
      subscriberIds: mapped.local.subscriberIds,
    };
    docRef.value?.restoreLocalState?.(pendingDocLocal.value);
    await nextTick();
    emitProgress();
  } catch (err) {
    console.warn('[SingbacPre] custom-form answer autofill failed:', err);
    lastAnswerCd.value = '';
  }
}

// 챗봇 위젯에서 폼/작성 선택 → formUpdates → chatFormData 로 도착.
watch(
  () => [props.chatFormData?.customFormCd, props.chatFormData?.customFormAnswerCd] as const,
  ([fCd, aCd]) => {
    if (aCd) {
      void autofillFromCustomFormAnswer(String(fCd || ''), String(aCd));
    } else {
      // Change 로 선택이 비워짐 → 다음 선택(같은 회사 포함)도 다시 자동채움되도록 가드 리셋.
      lastAnswerCd.value = '';
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    await loadCmBaseCodeOptions('B04');
    for (const [k, v] of Object.entries(fill.values)) {
      if (!/nationality/i.test(k)) continue;
      const raw = String(v || '').trim();
      if (!/^B\d{5}$/i.test(raw)) continue;
      const label = nationalityLabelForOutput(raw);
      if (label && label !== raw) fill.setValue(k, label);
    }
  } catch {
    /* ignore */
  }
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    const steps = cfg?.processSteps;
    taskConfigSteps.value = Array.isArray(steps) ? steps : [];
  } catch {
    /* ignore */
  }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}

function restoreState(s: any) {
  if (!s) return;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) {
      if (v != null) fill.setValue(k, String(v));
    }
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || '').trim();
    const valuesSnap = fill.exportPayload();
    // PRE — bare-leak 없음: 회사 단위 문서는 top-level 키만, Notice 는 ctrl_{id}/ 스코프만 사용.
    const baseForm = normalizeSingbacIncPreFormDataForWord({ ...valuesSnap });
    // 문서별 [[Signers]] — 스코프 키(dir_/sec_/ctrl_ + 'Signers')로 저장돼 있음. 문서 생성 시 부수별로 배정.
    const signersMap = signersCtx.exportAll();

    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SINGBAC_INCORPORATION_PRE',
      isMyPreset: props.selection?.isMyPreset === true,
      formData: baseForm,
      signers: signersMap,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Incorporation Pre',
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 생성된 부수 문서(같은 tmpId 다수)일 수 있음 → tmpId 기준 dedupe.
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const local: any = docRef.value?.gatherLocalState?.() || { controllerIds: [1], secretaryIds: [1] };
    const controllerIds: number[] = Array.isArray(local.controllerIds) && local.controllerIds.length
      ? local.controllerIds
      : [1];
    const secretaryIds: number[] = Array.isArray(local.secretaryIds) && local.secretaryIds.length
      ? local.secretaryIds
      : [1];
    const directorIds: number[] = Array.isArray(local.directorIds) && local.directorIds.length
      ? local.directorIds
      : [1];
    const companyLabel = String(baseForm.company_name || 'Company');

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    type SbKind = 'constitution' | 'form45b' | 'form45' | 'notice_controller' | 'other';
    // docCode(=DOC_TYPE, 운영자가 지정하는 안정 코드) 를 우선 보고, templateName(원본 파일명)·directory 도 함께 판정.
    //   업로드 파일명이 임의(예: "Draft Form_45_.docx")여도 오분류되지 않도록 세 식별자를 결합해 매칭한다.
    function classifyDoc(d: any): SbKind {
      // 밑줄/하이픈/점을 공백으로 정규화 — "Draft Form_45_.docx" 같은 파일명도 'form 45' 로 매칭되게.
      const n = [
        String(d?.docCode || ''),
        String(d?.templateName || d?.docCode || ''),
        String(d?.directory || ''),
      ]
        .join(' ')
        .toLowerCase()
        .replace(/[_\-.]+/g, ' ');
      if (/constitution|articles?\s+of\s+assoc/.test(n)) return 'constitution';
      // 45B(비서) 를 45(이사) 보다 먼저 판정.
      if (/45\s*b|f\s*45\s*b|consent.*secret|secret.*consent|173c\s*\(?b\)?/.test(n)) return 'form45b';
      if (/form\s*45|f\s*45|173c\s*\(?a\)?|consent.*director|director.*consent/.test(n)) return 'form45';
      if (/notice.*controll|controll|ubo|386ag|reg.*controller/.test(n)) return 'notice_controller';
      return 'other';
    }

    function companyForm(): Record<string, any> {
      const companyFd: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        companyFd[k] = v;
      }
      return normalizeSingbacIncPreFormDataForWord(companyFd);
    }

    /** ctrl_{id}/ 스코프 — top-level(회사 공통) + 그 스코프 값(bare) 만. */
    function formDataForScope(scopePrefix: string): Record<string, any> {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        out[k] = v;
      }
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).startsWith(scopePrefix)) continue;
        const bare = String(k).slice(scopePrefix.length);
        if (bare) out[bare] = v;
      }
      return normalizeSingbacIncPreFormDataForWord(out);
    }

    function pushOnce(
      d: any,
      formData: Record<string, any>,
      personName: string,
      labelSuffix = '',
      signers?: any[],
    ) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      // 생성 파일명 접미사(" — <사람/역할>") 제거 — 재저장 시 누적 방지. 원본명은 " - "(hyphen)만 쓰므로 안전.
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '').split(' — ')[0].trim();
      docsToSave.push({
        templateId: tid,
        label: labelSuffix ? `${baseLabel} — ${labelSuffix}` : baseLabel,
        personName,
        formData: normalizeSingbacIncPreFormDataForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    /**
     * 부수 문서 이름 접미사 — "Director 1" 대신 그 사람 이름("Draft Form_45_ — YUNSEOK KIM").
     *   이름이 비면 "Director 1" 처럼 번호, 같은 이름이 또 나오면 " (2)" 를 붙여 파일명이 겹치지 않게 한다.
     */
    const usedSuffix = new Set<string>(); // "문서종류|이름" — 같은 사람이 이사이자 컨트롤러여도 문서가 다르면 겹침 아님
    function personSuffix(name: unknown, fallback: string, docKind: string): string {
      const base = String(name ?? '').trim() || fallback;
      let s = base;
      for (let n = 2; usedSuffix.has(`${docKind}|${s.toUpperCase()}`); n++) s = `${base} (${n})`;
      usedSuffix.add(`${docKind}|${s.toUpperCase()}`);
      return s;
    }

    /**
     * 부수 스코프(dir_/sec_/ctrl_)의 [[Signers]] — 화면에서 지정/편집한 서명자 우선,
     * 없으면 그 부수 본인 성명으로 1명 fallback seed.
     */
    function signersForScope(scopePrefix: string, seedName: string, title: string): any[] | undefined {
      const list = signersMap[`${scopePrefix}Signers`];
      if (list?.length) return list;
      const seeded = buildOneSigner(`${scopePrefix}Signers-self`, seedName, title);
      return seeded.length ? seeded : undefined;
    }

    // 진단: 링크된 템플릿(docCode/templateName/tmpId)과 부수 카운트 — 갯수/바인딩 이슈 원인 파악용.
    console.info(
      '[SingbacPre] linked docs',
      docs.map((d: any) => ({
        docCode: d?.docCode,
        templateName: d?.templateName,
        tmpId: d?.tmpId ?? d?.templateId,
        kind: classifyDoc(d),
      })),
      { directors: directorIds.length, secretaries: secretaryIds.length, controllers: controllerIds.length },
    );

    for (const d of docs) {
      const kind = classifyDoc(d);
      // Notice to Controller — registrable controller 수만큼(부수) 생성(ctrl_{id}/ 스코프).
      if (kind === 'notice_controller') {
        for (const id of controllerIds) {
          const fd = formDataForScope(`ctrl_${id}/`);
          // 수동 입력 시엔 (a) Name in full(controller_full_name) 또는 (b) 법인명만 채워질 수 있다 — 모두 본다.
          const ctrlName = String(
            fd.confirming_controller_name ||
              fd.controller_name ||
              fd.controller_full_name ||
              fd.controller_entity_name ||
              '',
          ).trim();
          const person = ctrlName || `Controller ${id}`;
          const signers = signersForScope(`ctrl_${id}/`, person, 'Controller/UBO');
          pushOnce(d, fd, person, personSuffix(ctrlName, `Controller ${id}`, 'ctrl'), signers);
        }
        continue;
      }
      // Form 45B — 비서 수만큼(부수) 생성(sec_{id}/ 스코프).
      if (kind === 'form45b') {
        for (const id of secretaryIds) {
          const fd = formDataForScope(`sec_${id}/`);
          const person = String(fd.secretary_name || `Secretary ${id}`);
          const signers = signersForScope(`sec_${id}/`, person, 'Secretary');
          pushOnce(d, fd, person, personSuffix(fd.secretary_name, `Secretary ${id}`, 'sec'), signers);
        }
        continue;
      }
      // Form 45 — 이사 수만큼(부수) 생성(dir_{id}/ 스코프).
      if (kind === 'form45') {
        for (const id of directorIds) {
          const fd = formDataForScope(`dir_${id}/`);
          const person = String(fd.director_name || `Director ${id}`);
          const signers = signersForScope(`dir_${id}/`, person, 'Director');
          pushOnce(d, fd, person, personSuffix(fd.director_name, `Director ${id}`, 'dir'), signers);
        }
        continue;
      }
      // 회사 단위 문서(Constitution) — 1부.
      pushOnce(d, companyForm(), companyLabel);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[SingbacPre] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[SingbacPre] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[SingbacPre] create failed', e);
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="singbac-pre-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Incorporation Pre' }}</div>
        <div class="co-subtitle">Singbac — Incorporation Pre · live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change entity</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field
        v-model="titleModel"
        density="compact"
        variant="outlined"
        hide-details
        label="Task Title"
        placeholder="e.g. Singbac Incorporation Pre — NewCo Pte. Ltd."
      />
    </div>

    <div class="co-doc">
      <!-- 커스텀폼 데이터 드래그 팔레트는 챗봇(ChatCustomFormPicker) 안에 표시된다. -->
      <SingbacIncorporationPrePackageDoc ref="docRef" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn
        color="primary"
        class="text-none"
        :loading="creating"
        :disabled="!effectiveTitle || creating"
        @click="createTask"
      >
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.singbac-pre-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.singbac-pre-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-entity { font-size: 12.5px; color: #42506a; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
